/**
 * Splits a paragraph into discrete sentences for bullet-style rendering.
 *
 * Primary pass uses sentence-terminator lookbehinds (skipping common
 * abbreviations like "U.S." or "Mr.") so well-punctuated copy splits cleanly.
 *
 * Fallback: when the snippet has no terminal punctuation (a common shape in
 * Offerings copy, e.g. `"100% online, ACBSP-accredited, eight concentrations"`),
 * the primary split returns a single sentence. To keep the bullet experience
 * usable in that case, fall back to a clause split on `;` or `, `. The fallback
 * only kicks in when the primary returns ≤ 1 sentence, so well-punctuated copy
 * is never re-split.
 */
export function paragraphToSentencesArray(paragraph: string): string[] {
  if (!paragraph) return [];

  const sentences = paragraph
    .split(/(?<!\w\.\w.)(?<![A-Z][a-z]\.)(?<=\.|\?|!)\s/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (sentences.length <= 1) {
    const clauseSplit = paragraph
      .split(/;\s*|,\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (clauseSplit.length > 1) return clauseSplit;
  }

  return sentences;
}

/** Escapes regex metacharacters so a literal keyword (e.g. `$21K`, `(BCBA)`, `2-year`) matches itself. */
const escapeRegex = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Compiled-regex cache keyed by the joined keyword list. Keyword catalogs are
 * static module-level constants in practice, so the cache holds one entry per
 * catalog and skips the ~1,900-entry alternation rebuild on every card render.
 */
const keywordRegexCache = new Map<string, RegExp>();

const getKeywordRegex = (keywords: readonly string[]): RegExp => {
  const key = keywords.join('|');
  const cached = keywordRegexCache.get(key);
  if (cached) return cached;
  const regex = new RegExp(
    `\\b(${keywords.map(escapeRegex).join('|')})\\b`,
    'gi',
  );
  keywordRegexCache.set(key, regex);
  return regex;
};

/** Wraps each occurrence of a curated keyword in `<b>` tags for inline emphasis. */
export const highLightKeywords = (
  text: string = '',
  keywords?: readonly string[],
): string => {
  if (!keywords || keywords.length === 0) return text;
  const regex = getKeywordRegex(keywords);
  regex.lastIndex = 0;
  return text.replace(regex, '<b>$1</b>');
};

export interface KeywordSegment {
  text: string;
  bold: boolean;
}

/**
 * Tokenizes text into segments of plain text and keyword matches.
 * Unlike `highLightKeywords`, this produces a data structure suitable for
 * rendering as safe React text nodes + `<b>` elements without innerHTML.
 */
export const tokenizeKeywords = (
  text: string = '',
  keywords?: readonly string[],
): KeywordSegment[] => {
  if (!text) return [];
  if (!keywords || keywords.length === 0) return [{ text, bold: false }];

  const regex = getKeywordRegex(keywords);
  regex.lastIndex = 0;

  const segments: KeywordSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), bold: false });
    }
    segments.push({ text: match[0], bold: true });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold: false });
  }

  return segments;
};
